import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComments,
  faSearch,
  faArrowLeft,
  faUserInjured,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import ChatWindow from "../Patient/ChatWindow";
import styles from "./DoctorPortal.module.css";

const DoctorMessages = ({ doctor, initialPatient = null }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(initialPatient);

  const token = localStorage.getItem("token");
  const docId = localStorage.getItem("userId");

  useEffect(() => {
    if (initialPatient) {
      setSelectedPatient(initialPatient);
    }
  }, [initialPatient]);

  useEffect(() => {
    const fetchRooms = async () => {
      if (!docId || !token) return;
      try {
        const response = await axios.get(`/api/chat/rooms/${docId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const fetchedRooms = response.data.rooms || [];
        // Sort most recent first
        fetchedRooms.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        setRooms(fetchedRooms);
      } catch (error) {
        console.error("Error fetching doctor chat rooms:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, [docId, token]);

  const filteredRooms = rooms.filter((room) => {
    const p = room.patientId;
    if (!p) return false;
    const name = `${p.firstName || ""} ${p.lastName || ""}`.trim() || p.name || "";
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    }
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const getInitials = (p) => {
    if (!p) return "P";
    const f = p.firstName ? p.firstName[0] : (p.name ? p.name[0] : "P");
    const l = p.lastName ? p.lastName[0] : "";
    return `${f}${l}`.toUpperCase();
  };

  return (
    <div>
      {/* View Header */}
      <div className={styles.viewHeader}>
        <div>
          <h1 className={styles.viewTitle}>Patient Communications</h1>
          <p className={styles.viewSubtitle}>
            Real-time encrypted WhatsApp-style messaging with your patients
          </p>
        </div>
      </div>

      {/* WhatsApp Hub Container */}
      <div
        className={styles.glassCard}
        style={{
          padding: 0,
          display: "flex",
          flexDirection: "column",
          height: "700px",
          maxHeight: "80vh",
          overflow: "hidden",
          border: "1px solid #cbd5e1",
        }}
      >
        {/* Conversation List */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "rgba(248, 250, 252, 0.6)" }}>
          {/* Search Bar */}
          <div style={{ padding: "1rem", borderBottom: "1px solid #e2e8f0" }}>
            <div style={{ position: "relative" }}>
              <FontAwesomeIcon
                icon={faSearch}
                style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
              />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.inputField}
                style={{ paddingLeft: "2.25rem", borderRadius: "9999px", fontSize: "0.88rem" }}
              />
            </div>
          </div>

          {/* Rooms List */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {loading ? (
              <p style={{ padding: "2rem 1rem", textAlign: "center", color: "#64748b", fontStyle: "italic" }}>
                Loading conversations...
              </p>
            ) : filteredRooms.length === 0 ? (
              <div style={{ padding: "3rem 1rem", textAlign: "center", color: "#64748b" }}>
                <FontAwesomeIcon icon={faComments} style={{ fontSize: "2rem", color: "#cbd5e1", marginBottom: "0.5rem" }} />
                <p style={{ margin: 0, fontWeight: 600, fontSize: "0.9rem" }}>No messages found</p>
              </div>
            ) : (
              filteredRooms.map((room) => {
                const p = room.patientId;
                const fullName = `${p.firstName || ""} ${p.lastName || ""}`.trim() || p.name || "Patient";

                return (
                  <div
                    key={room._id}
                    onClick={() => setSelectedPatient(p)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "1.2rem 1.5rem",
                      borderBottom: "1px solid #f1f5f9",
                      cursor: "pointer",
                      background: "transparent",
                      transition: "all 0.2s ease",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = "#ffffff";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "50%",
                        background: "#e2e8f0",
                        color: "#0f172a",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: "1rem",
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(p)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span style={{ fontWeight: 800, color: "#0f172a", fontSize: "1rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {fullName}
                        </span>
                        <span style={{ fontSize: "0.78rem", color: "#94a3b8", fontWeight: 650 }}>
                          {formatTime(room.updatedAt)}
                        </span>
                      </div>
                      <p style={{ margin: "4px 0 0 0", fontSize: "0.88rem", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {room.lastMessage || "Started a conversation"}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Full-Screen Chat Overlay Modal when patient is selected */}
      {selectedPatient && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 99999,
            background: "#ffffff",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Full-Screen Top Header Bar */}
          <div
            style={{
              padding: "0.85rem 1.5rem",
              borderBottom: "1px solid #e2e8f0",
              background: "#f8fafc",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "50%",
                  background: "#2d6a4f",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: "1rem",
                }}
              >
                {getInitials(selectedPatient)}
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#0f172a" }}>
                  {`${selectedPatient.firstName || ""} ${selectedPatient.lastName || ""}`.trim() || selectedPatient.name || "Patient"}
                </h3>
                <span style={{ fontSize: "0.78rem", color: "#2d6a4f", fontWeight: 700, display: "flex", alignItems: "center", gap: "6px" }}>
                  <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#2d6a4f", display: "inline-block" }}></span>
                  Online Consultation Room
                </span>
              </div>
            </div>

            {/* Cross Close Button on Top Right */}
            <button
              type="button"
              onClick={() => setSelectedPatient(null)}
              style={{
                width: "42px",
                height: "42px",
                borderRadius: "50%",
                background: "#f1f5f9",
                border: "1px solid #cbd5e1",
                color: "#475569",
                fontSize: "1.25rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                outline: "none",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "#ef4444";
                e.currentTarget.style.color = "#ffffff";
                e.currentTarget.style.borderColor = "#ef4444";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "#f1f5f9";
                e.currentTarget.style.color = "#475569";
                e.currentTarget.style.borderColor = "#cbd5e1";
              }}
              title="Close Chat Window"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* ChatWindow filling remaining full screen */}
          <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <ChatWindow
              patient={selectedPatient}
              onClose={() => setSelectedPatient(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorMessages;
